import * as React from "react";
import { Checkbox, Input, SearchableSelect, cn, useToast } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { MAP_DEFAULT_CENTER, isNumericId, parseTwoGisCoords } from "@racoon/lib";
import { useListPickupLocationTypesQuery } from "~/shared/api/organization-labels/organization-labels.grpc";
import { assignableLabels } from "~/shared/lib/organization-labels";
import {
  useCreatePickupLocationMutation,
  useUpdatePickupLocationMutation,
  useListPickupLocationsQuery,
} from "~/shared/api/pickup-locations/pickup-locations.grpc";
import {
  buildGrpcGeometry,
  generateCircleGeofence,
  withoutReplacedSuggestion,
  type Geofence,
} from "~/shared/lib/geofence";
import { LocationMap } from "~/shared/ui/location-map";
import { CoordChip } from "~/shared/ui/coord-chip";
import { TimeMask } from "~/shared/ui/time-mask";
import { OnboardingField } from "./onboarding-layout";
import { DISABLED_INPUT_CLASS, DISABLED_SELECT_CLASS } from "~/shared/ui/disabled-field";
import { ModeTabs } from "~/shared/ui/mode-tabs";
import { StepShell } from "./step-shell";
import { AROUND_THE_CLOCK_SCHEDULE, isAroundTheClock } from "~/shared/lib/availability-schedule";

type LocationMode = "new" | "existing";
type Priority = "low" | "medium" | "high";

const PRIORITY_VALUES: Record<Priority, ConnectProto.PickupLocationPriority> = {
  low: ConnectProto.PickupLocationPriority.LOW,
  medium: ConnectProto.PickupLocationPriority.MEDIUM,
  high: ConnectProto.PickupLocationPriority.HIGH,
};

type Props = {
  providerId?: number;
  organizationId?: bigint;
  contractId?: bigint;
  locationId?: bigint;
  /** Whether the form holds input that was never saved — the wizard confirms closing over it. */
  onDirtyChange: (dirty: boolean) => void;
  onSaved: (locationId: bigint) => void;
};

/**
 * Step 3 — the pickup location, drawn fresh or picked. A picked one is
 * not bound to the contract: that link lives on the container group now.
 * "Зона ответственности" and "Менеджер" from the design have no field here.
 */
export const LocationStep: React.FC<Props> = ({
  providerId,
  organizationId,
  contractId,
  locationId,
  onDirtyChange,
  onSaved,
}) => {
  const t = useT();
  const { toast } = useToast();

  const [mode, setMode] = React.useState<LocationMode>("new");
  const [name, setName] = React.useState("");
  // The provider's pickup location type id, "0" for none («Не указано»).
  const [typeId, setTypeId] = React.useState("0");
  const [address, setAddress] = React.useState("");
  const [gisLink, setGisLink] = React.useState("");
  const [is24h, setIs24h] = React.useState(true);
  const [workFrom, setWorkFrom] = React.useState("09:00");
  const [workTo, setWorkTo] = React.useState("20:00");
  const [priority, setPriority] = React.useState<Priority>("medium");
  const [geoGroup, setGeoGroup] = React.useState("");
  const [selectedExistingId, setSelectedExistingId] = React.useState<string>("");
  const [geofences, setGeofences] = React.useState<Geofence[]>([]);
  // Only a site created by this form may be updated from it; a bound existing one is just pointed at.
  const [createdId, setCreatedId] = React.useState<bigint>();

  const [createLocation, { isLoading: creating }] = useCreatePickupLocationMutation();
  const [updateLocation, { isLoading: updating }] = useUpdatePickupLocationMutation();

  // The step stays mounted across a customer change, and the provider-wide list below still has the old pick.
  React.useEffect(() => {
    setSelectedExistingId("");
  }, [organizationId]);

  // The provider's active sites: a new customer has none yet; the pick binds the site to their contract.
  const { currentData: locationList } = useListPickupLocationsQuery(
    {
      serviceProviderIds: providerId ? [BigInt(providerId)] : [],
      statuses: [ConnectProto.LocationStatus.ACTIVE],
    },
    { skip: !providerId || mode !== "existing" },
  );
  const existingLocations = React.useMemo(
    () =>
      (locationList?.pickupLocations ?? []).filter(
        (location) => location.status === ConnectProto.LocationStatus.ACTIVE,
      ),
    [locationList],
  );

  const linkCoords = React.useMemo(() => (gisLink ? parseTwoGisCoords(gisLink) : null), [gisLink]);
  // A pin placed, dragged or removed on the map wins over the link's point, until the link changes.
  const [placedPin, setPlacedPin] = React.useState<{
    forLink: string;
    at: { lat: number; lon: number } | null;
  } | null>(null);
  const coords = placedPin?.forLink === gisLink ? placedPin.at : linkCoords;
  const isSaved = locationId != null;
  const createdHere = isSaved && locationId === createdId;
  const isBusy = creating || updating;
  // The new-site draft survives a switch to the "existing" tab, so it counts there too.
  const isDirty =
    !isSaved &&
    Boolean(
      name.trim() ||
        address.trim() ||
        gisLink.trim() ||
        geoGroup.trim() ||
        placedPin?.at ||
        geofences.length,
    );
  React.useEffect(() => onDirtyChange(isDirty), [isDirty, onDirtyChange]);

  // Read-only echo of the picked site, so the pick can be confirmed.
  const selectedExisting = React.useMemo(
    () => existingLocations.find((location) => String(location.id) === selectedExistingId),
    [existingLocations, selectedExistingId],
  );

  const displayed =
    mode === "existing" && selectedExisting
      ? {
          name: selectedExisting.name,
          // An unset proto2 optional is missing at runtime, not 0n (normalizeBigInt copies own keys).
          typeId: String(selectedExisting.pickupLocationTypeId ?? 0),
          address: selectedExisting.address ?? "",
          gisLink: selectedExisting.gisLink ?? "",
          lat: selectedExisting.lat,
          lon: selectedExisting.lon,
          schedule: selectedExisting.availabilitySchedule ?? "",
          geoGroup: selectedExisting.geoGroup ?? "",
        }
      : undefined;

  // From the link, not the pin: dragging the pin must not pan the map under it.
  const mapCenter: [number, number] = displayed
    ? [displayed.lat, displayed.lon]
    : linkCoords
      ? [linkCoords.lat, linkCoords.lon]
      : [MAP_DEFAULT_CENTER.lat, MAP_DEFAULT_CENTER.lng];

  const existingLocationOptions = React.useMemo(
    () => existingLocations.map((location) => ({ id: String(location.id), name: location.name })),
    [existingLocations],
  );

  const { currentData: typesResponse } = useListPickupLocationTypesQuery(
    { ownerOrganizationIds: providerId ? [BigInt(providerId)] : [] },
    { skip: !providerId },
  );
  const shownTypeId = displayed?.typeId ?? typeId;
  const locationTypeOptions = React.useMemo(
    () => [
      { id: "0", name: t(Namespace.Settings, "organizationLabels.undefined") },
      ...assignableLabels(typesResponse?.labels, shownTypeId).map((label) => ({
        id: label.id.toString(),
        name: label.name,
      })),
    ],
    [t, typesResponse, shownTypeId],
  );

  const priorityOptions = React.useMemo(
    () => [
      { id: "low", name: t(Namespace.Customers, "onboarding.location.priorityLow") },
      { id: "medium", name: t(Namespace.Customers, "onboarding.location.priorityMedium") },
      { id: "high", name: t(Namespace.Customers, "onboarding.location.priorityHigh") },
    ],
    [t],
  );

  const availabilitySchedule =
    !is24h && workFrom && workTo ? `${workFrom} - ${workTo}` : AROUND_THE_CLOCK_SCHEDULE;

  // A fresh 2GIS link seeds a 50m ring, as the pickup-location card does; the
  // dispatcher can reshape or delete it, and a later link change never overwrites it.
  const seededForLink = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!linkCoords || seededForLink.current === gisLink) return;
    seededForLink.current = gisLink;
    setGeofences((current) =>
      current.length > 0
        ? current
        : [
            {
              id: `auto-${Date.now()}`,
              latlngs: generateCircleGeofence(linkCoords.lat, linkCoords.lon, 50),
            },
          ],
    );
  }, [linkCoords, gisLink]);

  const buildPayload = () => ({
    name: name.trim(),
    // Still required by the proto until the enum is dropped.
    type: ConnectProto.PickupLocationType.UNDEFINED,
    pickupLocationTypeId: isNumericId(typeId) ? BigInt(typeId) : 0n,
    ownerOrganizationId: organizationId,
    serviceProviderId: providerId ? BigInt(providerId) : undefined,
    lon: coords?.lon ?? 0,
    lat: coords?.lat ?? 0,
    geometry: buildGrpcGeometry(geofences),
    address: address.trim(),
    availabilitySchedule,
    notificationSchedule: "1d",
    priority: PRIORITY_VALUES[priority],
    geoGroup: geoGroup.trim(),
    gisLink: gisLink.trim(),
    contractId,
  });

  const handleCreate = async () => {
    if (!coords) {
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.location.invalidCoords"),
      });
      return;
    }
    try {
      const created = await createLocation(buildPayload()).unwrap();
      const newId = BigInt(created.id);
      setCreatedId(newId);
      onSaved(newId);
    } catch (error) {
      console.error("Failed to create pickup location", error);
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.location.createFailed"),
      });
    }
  };

  const handleUpdate = async () => {
    // Never over an existing site picked on the other tab: the draft here is not its data.
    if (locationId == null || !createdHere) return;
    // Without valid coordinates the payload would zero lat/lon and drop the geometry.
    if (!coords) {
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.location.invalidCoords"),
      });
      return;
    }
    try {
      await updateLocation({ id: locationId, ...buildPayload() }).unwrap();
      toast({ title: t(Namespace.Customers, "onboarding.location.updated") });
    } catch (error) {
      console.error("Failed to update pickup location", error);
      toast({
        variant: "destructive",
        title: t(Namespace.Customers, "onboarding.location.updateFailed"),
      });
    }
  };

  const handleSelectExisting = () => {
    if (selectedExisting) onSaved(BigInt(selectedExisting.id));
  };

  const isExistingMode = mode === "existing";
  /** Everything but the picker is read-only while an existing site is shown. */
  const readOnly = isExistingMode;

  const primary = isExistingMode
    ? {
        label: t(Namespace.Customers, "onboarding.location.pick"),
        onClick: handleSelectExisting,
        // Re-picking another site is allowed; the steps after follow the new one.
        disabled: !selectedExisting || String(selectedExisting.id) === String(locationId),
      }
    : {
        label: t(Namespace.Customers, "onboarding.location.create"),
        onClick: handleCreate,
        disabled: isSaved || isBusy || !name.trim() || !coords,
      };

  return (
    <StepShell
      primary={primary}
      // An existing site is only pointed at, never rewritten with this tab's draft.
      secondary={
        isSaved && createdHere && !isExistingMode
          ? {
              label: t(Namespace.Customers, "onboarding.location.update"),
              onClick: handleUpdate,
              disabled: isBusy || !name.trim() || !coords,
            }
          : undefined
      }
      bodyClassName="!p-0 flex !overflow-hidden"
    >
      <div className="flex w-[640px] shrink-0 flex-col gap-4 overflow-y-auto p-4">
        <ModeTabs
          value={mode}
          onChange={setMode}
          options={[
            { value: "new", label: t(Namespace.Customers, "onboarding.location.tabNew") },
            { value: "existing", label: t(Namespace.Customers, "onboarding.location.tabExisting") },
          ]}
        />

        {isExistingMode && (
          <OnboardingField
            label={t(Namespace.Customers, "onboarding.location.pick")}
            htmlFor="onboarding-existing-location"
          >
            <SearchableSelect
              id="onboarding-existing-location"
              value={selectedExistingId}
              onChange={setSelectedExistingId}
              options={existingLocationOptions}
              placeholder={t(Namespace.Customers, "onboarding.selectPlaceholder")}
              searchPlaceholder={t(Namespace.Customers, "searchPlaceholder")}
            />
          </OnboardingField>
        )}

        <OnboardingField
          label={t(Namespace.Customers, "onboarding.location.name")}
          htmlFor="onboarding-location-name"
          required={!readOnly}
        >
          <Input
            id="onboarding-location-name"
            value={displayed?.name ?? name}
            disabled={readOnly}
            className={DISABLED_INPUT_CLASS}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
        </OnboardingField>

        <OnboardingField
          label={t(Namespace.Customers, "onboarding.location.type")}
          htmlFor="onboarding-location-type"
        >
          <SearchableSelect
            id="onboarding-location-type"
            value={shownTypeId}
            disabled={readOnly}
            triggerExtraClassName={DISABLED_SELECT_CLASS}
            onChange={setTypeId}
            options={locationTypeOptions}
            placeholder={t(Namespace.Customers, "onboarding.selectPlaceholder")}
          />
        </OnboardingField>

        <OnboardingField
          label={t(Namespace.Customers, "onboarding.location.address")}
          htmlFor="onboarding-location-address"
        >
          <Input
            id="onboarding-location-address"
            value={displayed?.address ?? address}
            disabled={readOnly}
            className={DISABLED_INPUT_CLASS}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)}
          />
        </OnboardingField>

        <OnboardingField
          label={t(Namespace.Customers, "onboarding.location.gisLink")}
          htmlFor="onboarding-location-gis"
          required={!readOnly}
          alignTop
        >
          <div className="space-y-2">
            <Input
              id="onboarding-location-gis"
              value={displayed?.gisLink ?? gisLink}
              disabled={readOnly}
              className={DISABLED_INPUT_CLASS}
              placeholder="https://2gis.kz/almaty/geo/..."
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGisLink(e.target.value)}
            />
            <div className="flex gap-2">
              <CoordChip
                label={t(Namespace.PickupLocation, "latitude")}
                value={displayed ? String(displayed.lat) : coords ? String(coords.lat) : undefined}
              />
              <CoordChip
                label={t(Namespace.PickupLocation, "longitude")}
                value={displayed ? String(displayed.lon) : coords ? String(coords.lon) : undefined}
              />
            </div>
          </div>
        </OnboardingField>

        <OnboardingField label={t(Namespace.Customers, "onboarding.location.workTime")}>
          <div className="flex items-center gap-3">
            <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                id="onboarding-location-24h"
                checked={displayed ? isAroundTheClock(displayed.schedule) : is24h}
                disabled={readOnly}
                onCheckedChange={(checked) => setIs24h(checked === true)}
              />
              {t(Namespace.Customers, "onboarding.location.allDay")}
            </label>
            <TimeField
              id="onboarding-location-work-from"
              prefix={t(Namespace.Customers, "onboarding.location.from")}
              value={displayed ? (displayed.schedule.split(" - ")[0] ?? "") : workFrom}
              disabled={readOnly || is24h}
              onChange={setWorkFrom}
            />
            <TimeField
              id="onboarding-location-work-to"
              prefix={t(Namespace.Customers, "onboarding.location.to")}
              value={displayed ? (displayed.schedule.split(" - ")[1] ?? "") : workTo}
              disabled={readOnly || is24h}
              onChange={setWorkTo}
            />
          </div>
        </OnboardingField>

        <OnboardingField
          label={t(Namespace.Customers, "onboarding.location.priority")}
          htmlFor="onboarding-location-priority"
        >
          <SearchableSelect
            id="onboarding-location-priority"
            value={priority}
            disabled={readOnly}
            triggerExtraClassName={DISABLED_SELECT_CLASS}
            onChange={(value) => setPriority(value as Priority)}
            options={priorityOptions}
          />
        </OnboardingField>

        <OnboardingField
          label={t(Namespace.Customers, "onboarding.location.geoGroup")}
          htmlFor="onboarding-location-geo-group"
        >
          <Input
            id="onboarding-location-geo-group"
            value={displayed?.geoGroup ?? geoGroup}
            disabled={readOnly}
            className={DISABLED_INPUT_CLASS}
            placeholder={t(Namespace.Customers, "onboarding.location.notSpecified")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGeoGroup(e.target.value)}
          />
        </OnboardingField>
      </div>

      <div className="min-w-0 flex-1 border-l border-slate-200">
        <LocationMap
          center={mapCenter}
          pin={
            displayed ? [displayed.lat, displayed.lon] : coords ? [coords.lat, coords.lon] : null
          }
          geofences={displayed ? [] : geofences}
          onChange={
            displayed
              ? undefined
              : (draft) => {
                  setPlacedPin({
                    forLink: gisLink,
                    at: draft.pin && { lat: draft.pin[0], lon: draft.pin[1] },
                  });
                  setGeofences(withoutReplacedSuggestion(draft.geofences));
                }
          }
        />
      </div>
    </StepShell>
  );
};

/** Bare input; the bordered chip around it is the field. */
const renderTimeInput = (id: string) => (inputProps: any) => (
  <input
    {...inputProps}
    id={id}
    className="w-12 border-none bg-transparent outline-none disabled:text-slate-400"
  />
);

const TimeField: React.FC<{
  id: string;
  prefix: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ id, prefix, value, disabled, onChange }) => (
  <div
    className={cn(
      "flex h-8 items-center gap-1 rounded-lg border px-2 text-sm",
      disabled ? "border-slate-300 bg-slate-200 text-slate-400" : "border-slate-200 bg-white",
    )}
  >
    <span className="text-icon-secondary">{prefix}</span>
    <TimeMask
      disabled={disabled}
      value={value}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    >
      {renderTimeInput(id)}
    </TimeMask>
  </div>
);
